import { Injectable, Signal, signal } from '@angular/core';
import { AccountServiceProxy, IsTenantAvailableInput, IsTenantAvailableOutput } from '@shared/service-proxies/service-proxies';
import { map, Observable } from 'rxjs';
import { AppTenantAvailabilityState } from '@shared/AppEnums';
import { PrivacyInfo } from '@privacy/types/privacy-info.type';
import { IubendaService } from '@privacy/services/iubenda.service';
import { palette, updatePrimaryPalette, updateSurfacePalette } from '@primeng/themes';
import { tenantThemes } from '@core/multi-tenancy/constants/tenant-themes';
import { normalizeString } from '@shared/helpers/string-utils';
import { LayoutStore } from '@shared/services/layout.store';
import { Title } from '@angular/platform-browser';

@Injectable({
    providedIn: 'root',
})
export class MultiTenancyService {
    private _tenancyUrl = signal<string>(this._initTenancyUrl());
    private _tenantId = signal<number>(abp.multiTenancy.getTenantIdCookie());
    private _tenantName = signal<string>(null);
    private _privacyInfo = signal<PrivacyInfo>(null);
    private _cookiesBannerInitialized = this.iubenda.cookiesBannerInitialized;

    constructor(
        private accountServiceProxy: AccountServiceProxy,
        private iubenda: IubendaService,
        private layout: LayoutStore,
        private title: Title,
    ) {}

    get tenancyUrl(): Signal<string> {
        return this._tenancyUrl.asReadonly();
    }

    get tenantId(): Signal<number> {
        return this._tenantId.asReadonly();
    }

    get tenantName(): Signal<string> {
        return this._tenantName.asReadonly();
    }

    set tenantName(value: string) {
        this._tenantName.set(value);
        this.title.setTitle(value);
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    get privacyInfo(): Signal<PrivacyInfo> {
        return this._privacyInfo.asReadonly();
    }

    checkTenantValidity(): Observable<boolean> {
        return this.getTenantId().pipe(
            map((res) => {
                const tenantAvailable = !!res.tenantId && res.state === AppTenantAvailabilityState.Available;
                if (tenantAvailable) {
                    this._saveTenantInfo(res);
                    if (!this._cookiesBannerInitialized()) {
                        this.iubenda.initCookiesBanner(res.siteId, res.cookiePolityId);
                    }
                    this.applyTenantTheme(res.tenancyName);
                    return true;
                } else {
                    return false;
                }
            }),
        );
    }

    applyTenantTheme(tenancyName: string): void {
        const theme = tenantThemes[normalizeString(tenancyName)];
        if (theme) {
            updatePrimaryPalette(palette(theme.primaryColor));
            if (theme.accentColor) {
                updateSurfacePalette(palette(theme.accentColor));
            }
            if (theme.logoName) {
                this.layout.setTenantLogoPath(tenancyName, theme.logoName);
            }
            if (theme.fontStyleClass) {
                this.layout.setGlobalFont(theme.fontStyleClass);
            }
        }
    }

    protected getTenantId(): Observable<IsTenantAvailableOutput> {
        const tenancyUrl = this.tenancyUrl();
        return this.accountServiceProxy.isTenantAvailable(
            new IsTenantAvailableInput({
                tenancyUrl,
            }),
        );
    }

    private _initTenancyUrl(): string {
        const { hostname, protocol } = window.location;
        return `${protocol}//${hostname}`;
    }

    private _saveTenantInfo(output: IsTenantAvailableOutput): void {
        abp.multiTenancy.setTenantIdCookie(output.tenantId);
        this._tenantId.set(output.tenantId);
        this.tenantName = output.tenancyName;
        this._privacyInfo.set({ ...output });
    }
}
